import { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { tokens } from "../styles";

/*
  An inline word that opens a small speech bubble above it showing a
  co-authorship chain. Each arrow links to the paper connecting two people.
  Opens on hover or keyboard focus, and toggles on tap for touch screens.

  chain = {
    people: ["Jarett Dewbury", "Person A", "Person B", "Paul Erdős"],
    papers: [{ title, url }, ...]   // one fewer than people
  }
*/

const rise = keyframes`
  from { opacity: 0; transform: translate(var(--dx), 3px); }
  to   { opacity: 1; transform: translate(var(--dx), 0); }
`;

const Wrap = styled.span`
  position: relative;
  display: inline-block;
`;
const Trigger = styled.button`
  font: inherit;
  color: ${tokens.ink};
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  text-decoration: underline dotted;
  text-decoration-color: ${tokens.soft};
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
  transition: color 0.2s, text-decoration-color 0.2s;
  &:hover, &[aria-expanded="true"] { color: ${tokens.blue}; text-decoration-color: ${tokens.blue}; }
`;
/* Invisible bridge under the bubble so moving the cursor up into it doesn't close it. */
const Bubble = styled.span`
  --dx: -50%;
  position: absolute;
  left: 50%;
  bottom: 100%;
  padding-bottom: 12px;
  transform: translate(var(--dx), 0);
  z-index: 20;
  animation: ${rise} 0.15s ease-out both;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const Card = styled.span`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: max-content;
  max-width: min(440px, calc(100vw - 32px));
  padding: 10px 14px;
  background: ${tokens.cream2};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(43, 43, 43, 0.08);
  font-size: 13px;
  line-height: 1.5;
  color: ${tokens.body};
  text-align: left;
  &::after {
    content: "";
    position: absolute;
    left: var(--caret, 50%);
    bottom: -5px;
    width: 10px;
    height: 10px;
    margin-left: -5px;
    background: ${tokens.cream2};
    transform: rotate(45deg);
  }
`;
const Label = styled.span`
  font-size: 11px;
  letter-spacing: 0.01em;
  color: ${tokens.soft};
`;
const Row = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 6px;
`;
const Person = styled.span`
  color: ${tokens.ink};
  white-space: nowrap;
`;
const Arrow = styled.a`
  display: inline-block;
  padding: 0 3px;
  color: ${tokens.blue};
  text-decoration: none;
  &:hover { color: ${tokens.ink}; }
`;

export default function CollabChain({ label, number, chain }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const bubbleRef = useRef(null);
  const closeTimer = useRef(0);

  const show = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const hideSoon = () => { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setOpen(false), 140); };

  // Keep the bubble inside the viewport; the caret stays pointed at the word.
  useLayoutEffect(() => {
    const b = bubbleRef.current;
    if (!open || !b) return;
    b.style.setProperty("--dx", "-50%");
    const r = b.getBoundingClientRect();
    let shift = 0;
    if (r.left < 12) shift = 12 - r.left;
    else if (r.right > window.innerWidth - 12) shift = window.innerWidth - 12 - r.right;
    b.style.setProperty("--dx", `calc(-50% + ${shift}px)`);
    b.firstChild.style.setProperty("--caret", `calc(50% - ${shift}px)`);
  }, [open]);

  // Close on Escape or a tap elsewhere.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("pointerdown", onDown); };
  }, [open]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <Wrap ref={wrapRef} onMouseEnter={show} onMouseLeave={hideSoon} onFocus={show} onBlur={(e) => { if (!wrapRef.current.contains(e.relatedTarget)) hideSoon(); }}>
      <Trigger type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)}>{label}</Trigger>
      {open && (
        <Bubble ref={bubbleRef}>
          <Card>
            <Label>Collaborative distance to Yann {label} is {number}</Label>
            <Row>
              {chain.people.map((name, i) => (
                <span key={i} style={{ display: "contents" }}>
                  <Person>{name}</Person>
                  {i < chain.papers.length && (
                    <Arrow
                      href={chain.papers[i].url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={chain.papers[i].title}
                      aria-label={`Paper linking ${name} and ${chain.people[i + 1]}: ${chain.papers[i].title}`}
                    >
                      →
                    </Arrow>
                  )}
                </span>
              ))}
            </Row>
          </Card>
        </Bubble>
      )}
    </Wrap>
  );
}